import Link from "next/link";
import Image from "next/image";
import { Address } from "@/dto/address";
import { localizedPhoneNumber } from "@/components/_localized/localizationUtils";
import { useEffect, useRef, useState } from "react";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";

export interface HeaderMerchantInfoContainerProps {
  merchantLogo: string;
  merchantName: string;
  isSimpleStorefront: boolean;
  isStandaloneGiftCard: boolean;
  merchantAddress: Address;
  merchantPhone: string;
}

export const HeaderMerchantInfoContainer = ({
  merchantLogo,
  merchantName,
  isSimpleStorefront,
  isStandaloneGiftCard,
  merchantAddress,
  merchantPhone,
}: HeaderMerchantInfoContainerProps) => {
  const [isMultiline, setIsMultiline] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const scanToOrderInfo = getFromLocalStorage<ScanToOrderInfo | null>(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  const pathname = "/";
  const queryParams = {
    order_type: scanToOrderInfo?.orderType,
    table_id: scanToOrderInfo?.tableId,
    employee_id: scanToOrderInfo?.employeeId,
  };
  const linkHref = {
    pathname: pathname,
    ...(scanToOrderInfo && { query: queryParams }),
  };

  useEffect(() => {
    const checkMultiline = () => {
      if (textRef.current) {
        const element = textRef.current;
        const style = window.getComputedStyle(element);
        const lineHeight = parseInt(style.lineHeight, 10);
        const elementHeight = element.clientHeight;
        setIsMultiline(elementHeight > lineHeight);
      }
    };

    checkMultiline();

    window.addEventListener("resize", checkMultiline);

    return () => {
      window.removeEventListener("resize", checkMultiline);
    };
  }, []);

  const merchantNameClasses = [
    "leading-normal",
    "font-normal",
    "break-all",
    "sm:max-w-none",
    isMultiline ? "text-[18px] sm:text-[32px]" : "text-[22px] sm:text-[32px]",
  ].join(" ");

  return (
    <div className={["flex", "flex-row", "sm:gap-6", "items-center"].join(" ")}>
      {!isStandaloneGiftCard ? (
        <Link href={linkHref}>
          <Image
            className={[
              "hidden sm:block h-[51px] w-[51px] sm:h-16 sm:w-16",
            ].join(" ")}
            src={merchantLogo}
            alt="merchantLogo"
            width={100}
            height={100}
          />
        </Link>
      ) : (
        <Image
          className={["hidden sm:block h-[51px] w-[51px] sm:h-16 sm:w-16"].join(
            " "
          )}
          src={merchantLogo}
          alt="merchantLogo"
          width={100}
          height={100}
        />
      )}
      {!isStandaloneGiftCard ? (
        <Link href={linkHref}>
          <div className="flex flex-col">
            <p ref={textRef} className={merchantNameClasses}>
              {merchantName}
            </p>
            {(isSimpleStorefront || isStandaloneGiftCard) && (
              <div>
                <p className="text-[15px]">
                  {merchantAddress.address1 +
                    merchantAddress.address2 +
                    ", " +
                    merchantAddress.city +
                    ", " +
                    merchantAddress.state +
                    " " +
                    merchantAddress.zip}
                </p>
                <p className="text-[15px]">
                  {localizedPhoneNumber(merchantPhone)}
                </p>
              </div>
            )}
          </div>
        </Link>
      ) : (
        <div className="flex flex-col">
          <p className={`text-2xl sm:text-[32px] leading-normal font-normal`}>
            {merchantName}
          </p>
          {(isSimpleStorefront || isStandaloneGiftCard) && (
            <div>
              <p className="text-[15px]">
                {merchantAddress.address1 +
                  (merchantAddress.address2
                    ? " " + merchantAddress.address2
                    : "") +
                  ", " +
                  merchantAddress.city +
                  ", " +
                  merchantAddress.state +
                  " " +
                  merchantAddress.zip}
              </p>
              <p ref={textRef} className="text-[15px]">
                {localizedPhoneNumber(merchantPhone)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
